/**
 * components/rightbar/TabBodySeat.tsx —— 一个 tab 的**正文座位**：注入座位输入面并按 `kind` 渲染已注册正文。
 *
 * 真源：`102:frontend_src/src/components/rightbar/TabBodySeat.vue`（70 行）——抄形态/尺寸/交互，译 Vue→React+TS。
 * 依据：设计件 §6.0-1「tab 正文｜按 `kind` 由注册的类型渲染（**keyed 座位**）」／§6.2 阶段二
 *      （正文按同一 `id` 键控注册）／§2.4（`RightTabBody.<type>` 输入 `useTabInfo()`）。
 * 纪律（逐条承源件）：
 *   · 🔴 **禁 `if (kind === …)` 分派**：正文组件一律经 `bodyFor(kind)` 查表；
 *   · 未登记正文的 kind ⇒ null ⇒ 由本座位兜底（「未登记类型」占位；与 nodeRegistry 的兜底纪律同款）；
 *   · 本座位**不创建 tab、不改布局、不发请求**：动作原样转给宿主（停靠面 store 在宿主侧）。
 *
 * ★20261011 PFK4·L-PF-B12（规划件 §4.9.2 #8／§7.3 #16）——**按类型保活**三配套：
 *   ① 隐藏而非卸载：`keepMounted` 类型的正文挂在**模块级座位记录**（`KEPT`：容器＋独立 React root）
 *      —— 切 tab／切会话／折叠（宿主卸载）只**脱离**容器（隐藏、零布局代价），DOM 与 root 不重建；
 *   ② 刷新命令绑定：座位输入面给 `info.refresh()`（宿主 `onRefresh` ／正文 `bodyProps.onRefresh` 择一），
 *      **切回**（再次挂载）自动触发一次刷新 ⇒ 保活面可刷新（新数据能进来）；
 *   ③ 关闭释放：正文可 `info.registerCloseHandler(fn)` 注册释放句柄；tab 关闭（`releaseSeat`／
 *      `releaseClosedSeats`）时逐个执行并销毁 root（registerCloseHandler 语义）。
 *
 * Vue→React 差异点（登记）：
 *   ① `provideTabInfo`/`useTabInfo`（provide/inject）⇒ **props 注入**（`info`，类型 `TabBodyInfo`）；
 *      缺注入的运行时守卫沿用源件报错文案（那是接线错误，不是用户错误）。
 *   ② `reactive(tabInfo)` ＋ `watch(props.tab, deep)` 同步 ⇒ React 派生制：每渲染由 `tab` 现算
 *      （导航/会话变化 ⇒ props 更新 ⇒ 正文重渲染，与源件「正文 watch 到变化」同效）。
 *   ③ 源件 `bodyFor` 在 `rightbar/tabBodies.js`（stage-1 kind→id → stage-2 id→组件 两级查表）；
 *      工作面禁建该件 ⇒ 注册表内聚本件（kind→组件一级查表），`bodyFor`/`registerBody` 形制保留，
 *      供 main 收编进统一 tab 类型注册表（DSH 口径：右栏＝tab 类型注册表）。
 *   ④ 零取数：正文的页数据一律由宿主经 `bodyProps` 注入（各正文自导出 props 类型）。
 */
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { FilesTabBody } from "./FilesTabBody";
import { ProgressTabBody } from "./ProgressTabBody";
import { TextTabBody } from "./TextTabBody";
import { isKeepMounted } from "./tabRegistry";

/** tab 记录（承源件 props.tab：`{ tabId, tabKind, params, address, title }`） */
export interface TabBodyRecord {
  tabId: string;
  tabKind: string;
  params?: Record<string, unknown> | null;
  address?: string | null;
  title?: string | null;
}

/** 座位输入面（承源件 `useTabInfo()` 注入面；字段与动作逐条同名同义） */
export interface TabBodyInfo {
  tabId: string;
  kind: string;
  params: Record<string, unknown>;
  address: string;
  sessionId: string | null;
  /** 打开资源地址（地址即身份；源件 `openAddress(address, kind?)`） */
  openAddress: (address: string, kind?: string) => void;
  /** 按 kind 开页 tab */
  openKind: (kind: string) => void;
  /** 关闭本 tab */
  closeSelf: () => void;
  /** ★B12 配套②：刷新命令（保活面可刷新；切回自动触发一次） */
  refresh: () => void;
  /** ★B12 配套③：关闭释放句柄注册（返回注销函数；tab 关闭时逐个执行） */
  registerCloseHandler: (handler: () => void) => () => void;
}

/** 正文组件的统一 props 根（每件同构：`info` ＋ 各自页数据/动作回调，见各正文导出类型） */
export interface TabBodyProps {
  /** 座位输入面（本座位恒注入满值；正文不得脱离座位装配——源件 useTabInfo 缺注入抛错同款） */
  info: TabBodyInfo;
}

/** 正文组件形制（kind ⇒ 渲染件） */
export type TabBodyComponent = React.ComponentType<TabBodyProps & Record<string, unknown>>;

/** id → 正文组件（源件 stage-2 注册表；kind 值＝stage-1 `kind`：files/progress/text） */
const BODIES = new Map<string, TabBodyComponent>();

/**
 * 注册一个 tab 正文。`kind` 必须与 stage-1 注册的 `kind` 相同（源件按 `id` 键控，本件按 kind 键控，见差异点③）。
 * @throws 同 kind 二次注册 ⇒ 抛错（承源件纪律）
 */
export function registerBody(kind: string, component: TabBodyComponent): void {
  if (BODIES.has(kind)) throw new Error(`tabBodies: duplicate id "${kind}"`);
  BODIES.set(kind, component);
}

/** 清空注册表（仅测试用；承源件 `_clearBodies`） */
export function _clearBodies(): void {
  BODIES.clear();
}

/** 内置正文注册（模块加载即完成；与 stage-1 内置类型一一对应：`builtin:files`/`builtin:progress`/`builtin:text`） */
registerBody("files", FilesTabBody as TabBodyComponent);
registerBody("progress", ProgressTabBody as TabBodyComponent);
registerBody("text", TextTabBody as TabBodyComponent);

/**
 * 查表：kind → 正文组件。
 * 未注册/非字符串 ⇒ null（不抛错、不兜底；兜底归本座位模板）。
 */
export function bodyFor(kind: string): TabBodyComponent | null {
  if (typeof kind !== "string") return null;
  return BODIES.get(kind) ?? null;
}

// ── ★B12 保活座位层（模块级：跨宿主卸载存活；仅 `keepMounted` 类型走此层）────────────────

/** 保活座位记录（key＝`<sessionId>:<tabId>`；容器脱离 DOM 时仍留内存 ⇒ DOM 不重建） */
interface KeptSeat {
  key: string;
  container: HTMLDivElement;
  root: Root | null;
  closeHandlers: Array<() => void>;
  /** 已挂载过（切回刷新只在第二次起触发；首挂是「开」不是「回」） */
  attachedOnce: boolean;
}

const KEPT = new Map<string, KeptSeat>();

/** 座位键（会话＋tab 两维身份；会话缺省＝空串） */
export function seatKeyOf(sessionId: string | null, tabId: string): string {
  return `${sessionId ?? ""}:${tabId}`;
}

function seatOf(key: string): KeptSeat {
  let seat = KEPT.get(key);
  if (!seat) {
    const container = document.createElement("div");
    container.className = "xg-tab-seat-kept";
    container.setAttribute("data-seat-key", key);
    seat = { key, container, root: null, closeHandlers: [], attachedOnce: false };
    KEPT.set(key, seat);
  }
  return seat;
}

function renderSeatBody(seat: KeptSeat, body: TabBodyComponent,
                        info: TabBodyInfo, bodyProps: Record<string, unknown>): void {
  if (typeof document === "undefined") return;
  if (!seat.root) seat.root = createRoot(seat.container);
  seat.root.render(React.createElement(body, { ...bodyProps, info }));
}

/**
 * **关闭释放**（B12 配套③）：执行该座位全部 `registerCloseHandler` 句柄并销毁 root/容器。
 * 句柄异常不外抛（释放链不能被单个句柄打断；异常吞掉但逐个执行完）。
 */
export function releaseSeat(key: string): void {
  const seat = KEPT.get(key);
  if (!seat) return;
  for (const handler of seat.closeHandlers.splice(0)) {
    try {
      handler();
    } catch {
      /* 释放句柄不抛（逐个执行完） */
    }
  }
  seat.container.remove();
  KEPT.delete(key);
  const root = seat.root;
  seat.root = null;
  if (root) {
    // 根清理让出当前提交（React 禁在渲染/提交中同步卸载他根——卸根走微任务，DOM/句柄释放仍是同步语义）
    queueMicrotask(() => root.unmount());
  }
}

/**
 * **关闭观测**（B12 配套③，宿主每渲染调）：当前会话里已从停靠面消失的 tab ⇒ 释放其保活座位。
 * 跨会话的在场座位**不**释放（他会话的停靠面状态不在本宿主视野；待其会话回前台时同规则结算）。
 */
export function releaseClosedSeats(sessionId: string | null, liveTabIds: readonly string[]): void {
  const mine = `${sessionId ?? ""}:`;
  for (const key of [...KEPT.keys()]) {
    if (!key.startsWith(mine)) continue;
    const tabId = key.slice(mine.length);
    if (!liveTabIds.includes(tabId)) releaseSeat(key);
  }
}

/** 在场保活座位（测试/判据读数用：只读快照） */
export function keptSeatKeys(): string[] {
  return [...KEPT.keys()];
}

/** 清空保活座位层（仅测试用；逐个走关闭释放语义） */
export function _resetSeatStore(): void {
  for (const key of [...KEPT.keys()]) releaseSeat(key);
}

/** 保活槽：把座位容器挂进当前槽位（挂载＝attach／卸载＝**脱离**，不销毁） */
function KeepSlot(props: {
  seatKey: string;
  body: TabBodyComponent;
  info: TabBodyInfo;
  bodyProps: Record<string, unknown>;
  onSwitchBack: () => void;
}): React.ReactElement {
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    const slot = ref.current;
    if (!slot) return undefined;
    const seat = seatOf(props.seatKey);
    if (seat.container.parentElement !== slot) slot.appendChild(seat.container);
    const wasAttached = seat.attachedOnce;
    seat.attachedOnce = true;
    renderSeatBody(seat, props.body, props.info, props.bodyProps);
    if (wasAttached) props.onSwitchBack();   // ★B12 配套②：切回可刷新
    return () => {
      // 卸载＝脱离（隐藏而非卸载）：容器留在模块级，DOM/root 不重建
      if (seat.container.parentElement === slot) seat.container.remove();
    };
  });
  return <div ref={ref} className="xg-tab-seat-slot" data-testid="rightbar-seat-slot" />;
}

export interface TabBodySeatProps {
  /** tab 记录（`{ tabId, tabKind, params, address, title }`） */
  tab: TabBodyRecord;
  /** 当前会话 id（页类型正文的数据作用域） */
  sessionId?: string | null;
  /** 查表函数（缺省＝内置 `bodyFor`；宿主可注入统一 tab 类型注册表的解析器） */
  bodyFor?: (kind: string) => TabBodyComponent | null;
  /** 宿主注入的页数据/动作（零取数；透传给正文组件的非 `info` props） */
  bodyProps?: Record<string, unknown>;
  /** 打开资源地址（承源件 emit `open-address`） */
  onOpenAddress?: (e: { address: string; kind?: string; fromTabId: string }) => void;
  /** 按 kind 开页 tab（承源件 emit `open-kind`） */
  onOpenKind?: (e: { kind: string; fromTabId: string }) => void;
  /** 关闭本 tab（承源件 emit `close-tab`） */
  onCloseTab?: (tabId: string) => void;
  /** 聚焦本 tab（承源件 emit `focus-tab`；源件本件内无触点，回调口保留不删） */
  onFocusTab?: (tabId: string) => void;
  /** ★B12 配套②：刷新命令宿主接线（缺省回落 `bodyProps.onRefresh`） */
  onRefresh?: (e: { tabId: string }) => void;
}

/** 一个 tab 的正文座位 */
export function TabBodySeat(props: TabBodySeatProps): React.ReactElement {
  const { tab, sessionId = null } = props;
  const bodyProps = props.bodyProps ?? {};
  const handlersRef = React.useRef<Array<() => void>>([]);

  /** 刷新命令绑定（宿主接线优先；缺省回落正文页数据里的 `onRefresh`——如 FilesTabBody 既有刷新钮） */
  const refresh = (): void => {
    if (props.onRefresh) {
      props.onRefresh({ tabId: tab.tabId });
      return;
    }
    const fallback = (bodyProps as { onRefresh?: unknown }).onRefresh;
    if (typeof fallback === "function") (fallback as () => void)();
  };

  /** 座位输入面（承源件 tabInfo 三动作：一律经宿主落状态；＋B12 两配套） */
  const info: TabBodyInfo = {
    tabId: tab.tabId,
    kind: tab.tabKind,
    params: tab.params ?? {},
    address: (tab.address || (tab.params && tab.params.address) || "") as string,
    sessionId,
    openAddress: (address, kind) => props.onOpenAddress?.({ address, kind, fromTabId: tab.tabId }),
    openKind: (kind) => props.onOpenKind?.({ kind, fromTabId: tab.tabId }),
    closeSelf: () => props.onCloseTab?.(tab.tabId),
    refresh,
    registerCloseHandler: (handler: () => void) => {
      const list = (body !== null && isKeepMounted(tab.tabKind))
        ? seatOf(seatKeyOf(sessionId, tab.tabId)).closeHandlers   // ★B12 配套③：保活座位随关闭释放
        : handlersRef.current;
      list.push(handler);
      return () => {
        const idx = list.indexOf(handler);
        if (idx >= 0) list.splice(idx, 1);
      };
    },
  };

  /** 正文组件（查表；未登记 ⇒ null） */
  const body = (props.bodyFor ?? bodyFor)(tab.tabKind);
  /** 保活类型 ⇒ 释放句柄归**座位记录**（tab 关闭才释放），不随槽位卸载误放 */
  const kept = body !== null && isKeepMounted(tab.tabKind);
  const keptKey = seatKeyOf(sessionId, tab.tabId);

  // 非保活座位：卸载即释放（registerCloseHandler 语义：unmount≡释放；保活座位归 `releaseSeat`）
  React.useEffect(() => {
    if (kept) return undefined;
    return () => {
      for (const handler of handlersRef.current.splice(0)) {
        try {
          handler();
        } catch {
          /* 释放句柄不抛 */
        }
      }
    };
  }, [kept]);

  if (body && kept) {
    return (
      <KeepSlot
        seatKey={keptKey}
        body={body}
        info={info}
        bodyProps={bodyProps}
        onSwitchBack={refresh}
      />
    );
  }

  return (
    <>
      {body ? (
        React.createElement(body, { key: tab.tabId, info, ...bodyProps })
      ) : (
        <p className="xg-tab-seat__unknown" data-testid="rightbar-tab-unknown">
          未登记的类型：{tab.tabKind}
        </p>
      )}
      <style>{STYLE}</style>
    </>
  );
}

/** 自包含样式（源件 `<style scoped>` 逐字译入；真名 token；`xg-tab-seat` 前缀隔离） */
import { tabBodySeatStyle as STYLE } from "../../styles/inlineStyles";

export default TabBodySeat;
